/**
 * Seletor compacto da organização ativa no topo.
 * O menu de troca só aparece quando a pessoa pertence a mais de uma organização.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useWorkspace } from '../../contexts/WorkspaceContext';

interface OrganizationSelectorProps {
  variant?: 'default' | 'compact';
  className?: string;
}

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ 
  variant = 'default',
  className = '' 
}) => {
  const { workspace, availableOrganizations, selectOrganization, isLoading } = useWorkspace();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const canSwitch = workspace?.permissions.canSwitchOrganization ?? false;
  const showMenu = canSwitch && availableOrganizations.length > 1;

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (isLoading || !workspace) return null;

  if (!showMenu) {
    return (
      <div
        className={`flex items-center gap-1.5 text-[var(--text-secondary)] ${className}`}
        title={workspace.organizationName}
      >
        <Building2
          size={variant === 'compact' ? 14 : 16}
          className="shrink-0 text-slate-500 dark:text-slate-400"
        />
        <span className={`max-w-[180px] truncate font-medium text-slate-700 dark:text-slate-300 ${variant === 'compact' ? 'text-[11px]' : 'text-sm'}`}>
          {workspace.organizationName}
        </span>
      </div>
    );
  }
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded border transition-all ${
          variant === 'compact' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
        } font-semibold ${
          open
            ? 'bg-blue-50/90 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 shadow-sm'
            : 'bg-white/90 dark:bg-slate-800/90 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm'
        } backdrop-blur-sm`}
      >
        <Building2 
          size={variant === 'compact' ? 14 : 16} 
          className={open ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'} 
        />
        <span className="max-w-[180px] truncate">{workspace.organizationName}</span>
        <ChevronDown 
          size={variant === 'compact' ? 14 : 16} 
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} 
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded shadow-2xl z-[100] py-2 animate-in fade-in zoom-in-95 duration-200">
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Selecionar Organização
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto py-2 px-2">
            {availableOrganizations.map(org => (
              <button
                key={org.id}
                onClick={() => { 
                  selectOrganization(org.id); 
                  setOpen(false); 
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded text-left transition-all ${
                  org.id === workspace.organizationId
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-sm'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50/80 dark:hover:bg-slate-700/50'
                }`}
              >
                <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-600">
                  {org.logo ? (
                    <img src={org.logo} alt="" className="w-6 h-6 rounded object-cover" />
                  ) : (
                    <Building2 size={16} className="text-slate-400 dark:text-slate-500" />
                  )}
                </div>
                <span className="flex-1 text-sm font-medium truncate">{org.name}</span>
                {org.id === workspace.organizationId && (
                  <Check size={16} className="text-blue-600 dark:text-blue-400 shrink-0" />
                )}
              </button>
            ))}
          </div>
          
          {/* Informação de perfil do usuário */}
          <div className="mt-2 px-4 py-2 border-t border-slate-100 dark:border-slate-700">
            <p className="text-[10px] text-slate-400 dark:text-slate-500">
              Perfil: <span className="font-semibold text-slate-600 dark:text-slate-400 uppercase">{workspace.userRole}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
